import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Eye, EyeIcon } from "lucide-react";

import { PageHeader } from "@shared/components/PageHeader";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Badge } from "@shared/ui/badge";
import { useGetMetersDetails } from "@features/mx/readCycleManagement/hooks";

export const MetersTab = () => {
  const { cycle_id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read search query and page from URL
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const currentPage = parseInt(searchParams.get("page") || "1");
  const { data: metersData } = useGetMetersDetails(
    cycle_id,
    currentPage,
    searchQuery
  );

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination based on filtered data
  useEffect(() => {
    const totalItems = metersData?.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [metersData?.count, currentPage]);

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search; // remove from URL if search is empty
    }

    // Reset to page 1 on new search
    delete newParams.page;

    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  // Define table columns - use useMemo to prevent re-creation
  const columns = useMemo<TableColumn<any>[]>(
    () => [
      {
        key: "meterNumber",
        header: "Meter No",
        sortable: true,
      },
      {
        key: "deviceNo",
        header: "Device No",
        sortable: true,
      },
      {
        key: "productCode",
        header: "Utility Service",
        sortable: true,
      },
      {
        key: "premiseName",
        header: "Premise",
        sortable: true,
      },
      {
        key: "consumerInfo",
        header: "Consumer",
        sortable: true,
        render: (value: any) => (
          <span>{(value?.firstName||"N/A") + " " + (value?.lastName||"N/A")}</span>
        ),
      },
      {
        key: "consumerInfo",
        header: "Account Number",
        sortable: true,
        render: (value: any) => <span>{value?.consumerNo || "N/A"}</span>,
      },
      {
        key: "installedOn",
        header: "Installed On",
        sortable: true,
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        render: (value) => (
          <span
            className={
              value === "normal"
                ? "text-green-600"
                : value === "faulty"
                ? "text-red-600"
                : value === "rcnt"
                ? "text-blue-600"
                : "text-gray-600"
            }
          >
            {value}
          </span>
        ),
      },
      {
        key: "meterLastReading",
        header: "Last Reading",
        sortable: true,
      },
    ],
    []
  );

  const actions: TableAction[] = [
    {
      label: "View",
      icon: <EyeIcon className="text-gray-500" />,
      onClick: (row) => {
        navigate(`/mx/device-management/meter/view/${row.id}`);
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <PageHeader
            title="Meters"
            subtitle="Detailed meter inventory with consumer, status, and reading information."
          />
        </div>
        <div className="flex justify-between items-center">
          <Badge variant="outline">{metersData?.count || 0} meters</Badge>
        </div>
      </div>

      <div className="space-y-4">
        <GenericTable
          data={metersData?.results || []}
          columns={columns}
          rowKey="id"
          actions={actions}
          actionsType="icons"
          emptyMessage="No meters found matching your criteria"
          pagination={pagination}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          searchPlaceholder="Search meters..."
        />
      </div>
    </div>
  );
};
